import { API } from '@ospace/shared'

import type {
  CancelRunResponse,
  ClearCrawlCacheResponse,
  DeduplicationSettingsResponse,
  HubspotOwnersResponse,
  NukeDomainCacheResponse,
  RunEventsResponse,
  RunGroupsResponse,
  RunsResponse,
  SearchQueryParams,
  SearchResponse,
  StartRunResponse,
  TaskOwnerSetting,
  TraceDetail,
  RunSummary,
} from '../types'

const cleanParams = (params: Record<string, string | number | undefined>) =>
  Object.fromEntries(
    Object.entries(params).filter(([, value]) => value !== undefined && value !== '')
  )

const DEDUPLICATION_API = 'deduplication'

export const DeduplicationService = {
  search: (params: SearchQueryParams) =>
    API.get(DEDUPLICATION_API, '/deduplication/search', {
      queryStringParameters: cleanParams(params as Record<string, string | number | undefined>),
    }) as Promise<SearchResponse>,

  getRuns: (params: { limit: number; offset: number; objectType?: 'company' | 'contact' }) =>
    API.get(DEDUPLICATION_API, '/deduplication/runs', {
      queryStringParameters: cleanParams(params),
    }) as Promise<RunsResponse>,

  startRun: (objectType: 'company' | 'contact') =>
    API.post(DEDUPLICATION_API, '/deduplication/runs', {
      body: { objectType },
    }) as Promise<StartRunResponse>,

  getRun: (runId: string) =>
    API.get(DEDUPLICATION_API, `/deduplication/runs/${runId}`, {}) as Promise<RunSummary>,

  getRunGroups: (
    runId: string,
    params: { limit: number; offset: number; terminal?: string; status?: string; q?: string }
  ) =>
    API.get(DEDUPLICATION_API, `/deduplication/runs/${runId}/groups`, {
      queryStringParameters: cleanParams(params),
    }) as Promise<RunGroupsResponse>,

  getGroup: (runId: string, groupTraceId: string) =>
    API.get(
      DEDUPLICATION_API,
      `/deduplication/runs/${runId}/groups/${groupTraceId}`,
      {}
    ) as Promise<TraceDetail>,

  getRunEvents: (
    runId: string,
    params: { limit: number; cursor?: string; phase?: string; action?: string; event?: string }
  ) =>
    API.get(DEDUPLICATION_API, `/deduplication/runs/${runId}/events`, {
      queryStringParameters: cleanParams(params),
    }) as Promise<RunEventsResponse>,

  cancelRun: (runId: string) =>
    API.post(
      DEDUPLICATION_API,
      `/deduplication/runs/${runId}/cancel`,
      {}
    ) as Promise<CancelRunResponse>,

  getSettings: () =>
    API.get(
      DEDUPLICATION_API,
      '/deduplication/settings',
      {}
    ) as Promise<DeduplicationSettingsResponse>,

  listHubspotOwners: (q?: string) =>
    API.get(DEDUPLICATION_API, '/deduplication/hubspot/owners', {
      queryStringParameters: q ? { q } : {},
    }) as Promise<HubspotOwnersResponse>,

  updateTaskOwner: (taskOwner: TaskOwnerSetting) =>
    API.put(DEDUPLICATION_API, '/deduplication/settings/task-owner', {
      body: taskOwner,
    }) as Promise<DeduplicationSettingsResponse>,

  updateReviewTasksEnabled: (enabled: boolean) =>
    API.put(DEDUPLICATION_API, '/deduplication/settings/review-tasks', {
      body: { enabled },
    }) as Promise<Pick<DeduplicationSettingsResponse, 'reviewTasksEnabled' | 'reviewTasksCanRun'>>,

  clearCrawlCache: () =>
    API.post(
      DEDUPLICATION_API,
      '/deduplication/settings/clear-crawl-cache',
      {}
    ) as Promise<ClearCrawlCacheResponse>,

  nukeDomainCache: () =>
    API.post(
      DEDUPLICATION_API,
      '/deduplication/settings/nuke-domain-cache',
      {}
    ) as Promise<NukeDomainCacheResponse>,
}
